import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { NotifierGateway } from 'src/app/application/gateway/NotifierGateway';
import { CityService } from 'src/app/application/service/CityService';
import { City } from 'src/app/domain/models/City';
import { Country } from 'src/app/domain/models/Country';
import { Notifier } from 'src/app/domain/models/Notifier';
import { BasicUser } from 'src/app/domain/models/User';
import { Routes } from 'src/app/infrastructure/routes';
import { formLimit } from 'src/app/ui/util/form-limit';

@Component({
  selector: 'app-notifier-form',
  templateUrl: './notifier-form.component.html',
  styleUrls: ['./notifier-form.component.css'],

})
export class NotifierFormComponent implements OnInit, AfterViewInit {
  title: string = ''
  countries: Country[] = []
  cities: City[] = []
  submit = false
  loading = false
  notifierId = 0
  notifier: Notifier | undefined

  profileUser: BasicUser | undefined

  breadcrumbMenuItems: MenuItem[] = [{ label: '' }]
  breadcrumbMenuItems2: MenuItem[] = [{ label: '' }]
  breadcrumbMenuItems2Edit: MenuItem[] = [{ label: '' }]
  breadcrumbHome: MenuItem = { label: ''}

  formGroup: FormGroup = this._formBuilder.group({
    name: ['', Validators.required],
    vat: [''],
    contactCountryId: ['', Validators.required],
    contactCity: [''],
    cityId: [''],
    contactName: ['', Validators.required],
    contactAddress: ['', Validators.required],
    contactPhone: [''],
    contactEmail: ['', [Validators.required, Validators.email]],
  })

  formLimit = formLimit

  constructor(private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _messageService: MessageService,
    private _authService: AuthGateway,
    private _countryService: CountryGateway,
    private _notifierService: NotifierGateway,
    public translate: TranslateService,
    private _cityService: CityService) {

  }

  ngOnInit(): void {

    this.profileUser = this._authService.getUser()

    let breadcrumbStep1ItemLabel1 = 'Notify'
    let breadcrumbStep1ItemLabel2 = 'Create notify'
    let breadcrumbStep1ItemLabel2Edit = 'Edit notify'
    let homeItemLabel = 'User panel'

    if(this.profileUser) {
      switch(this.profileUser.lang) {
        case 'en':
          breadcrumbStep1ItemLabel1 = 'Notify'
          breadcrumbStep1ItemLabel2 = 'Create notify'
          breadcrumbStep1ItemLabel2Edit = 'Edit notify'
          homeItemLabel = 'User panel'
          break
        case 'es':
          breadcrumbStep1ItemLabel1 = 'Notify'
          breadcrumbStep1ItemLabel2 = 'Crear notify'
          breadcrumbStep1ItemLabel2Edit = 'Editar notify'
          homeItemLabel = 'Panel de usuario'
          break
      }
    }

    this.breadcrumbMenuItems = [{ label: breadcrumbStep1ItemLabel1, disabled: true }]
    this.breadcrumbMenuItems2 = [
      { label: breadcrumbStep1ItemLabel1, routerLink: ['/' + Routes.userNotifiers]},
      { label: breadcrumbStep1ItemLabel2, disabled: true }
    ]
    this.breadcrumbMenuItems2Edit = [
      { label: breadcrumbStep1ItemLabel1, routerLink: ['/' + Routes.userNotifiers]},
      { label: breadcrumbStep1ItemLabel2Edit, disabled: true }
    ]
    this.breadcrumbHome = { label: homeItemLabel, routerLink: '/' + Routes.userPanelPage}


    this._activatedRoute.params.subscribe(params => {
      this.notifierId = params['notifierId']
    })

    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Country.ManyRetrieveError') });
        console.log(e)
      },
      complete: () => console.info('Country query successfully')
    })
  }

  ngAfterViewInit(): void {
    if(this.notifierId && this.notifierId > 0) {
      this.title = this.translate.instant('UserPages.UserNotifies.NotifierForm.EditTitle')
      this._notifierService.getById(this.notifierId).subscribe({
        next: (v) => {
          this.notifier = v
          this.loadForm()
        },
        error: (e) => {
          console.log(e)
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Notify.SingleRetrieveError') });
        },
        complete: () => console.info('Notify query successfully')
      })
    }
    else {
      this.title = this.translate.instant('UserPages.UserNotifies.NotifierForm.CreateTitle')
      this.notifier = {}
    }
  }

  loadForm() {
    if(this.notifier) {
      if(this.notifier.contactCountry){
        this.loadCities(this.notifier.contactCountry.id)
      }
      this.formGroup.controls["name"].setValue(this.notifier.companyName)
      this.formGroup.controls["vat"].setValue(this.notifier.vat)
      this.formGroup.controls["contactName"].setValue(this.notifier.contactName)
      this.formGroup.controls["contactAddress"].setValue(this.notifier.contactAddress)
      this.formGroup.controls["contactEmail"].setValue(this.notifier.contactEmail)
      this.formGroup.controls["contactPhone"].setValue(this.notifier.contactPhone)
      this.formGroup.controls["contactCity"].setValue(this.notifier.contactCity)
      this.formGroup.controls["cityId"].setValue(this.notifier.city?.id)
      this.formGroup.controls["contactCountryId"].setValue(this.notifier.contactCountry?.id)
    }
  }

  save() {
    this.submit = true
    this.loading = true
    if(this.formGroup.valid && this.notifier) {
      this.notifier.companyName = this.formGroup.controls["name"].value
      this.notifier.vat = this.formGroup.controls["vat"].value
      this.notifier.contactName = this.formGroup.controls["contactName"].value
      this.notifier.contactAddress = this.formGroup.controls["contactAddress"].value
      this.notifier.contactEmail = this.formGroup.controls["contactEmail"].value
      this.notifier.contactPhone = this.formGroup.controls["contactPhone"].value
      this.notifier.contactCity = this.formGroup.controls["contactCity"].value
      this.notifier.contactCountryId = this.formGroup.controls["contactCountryId"].value
      this.notifier.cityId = this.formGroup.controls["cityId"].value

      if(this.notifierId && this.notifierId > 0) {
        //update
        this._notifierService.update(this.notifier).subscribe({
          next: (v) => {
            this.notifier = v
            this._messageService.add({ severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.Notify.EditSuccess') })
            this.loading = false
            this.submit = false
          },
          error: (e) => {
            this.loading = false
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.Notify.EditError') });
          },
          complete: () =>  {
            this.loading = false
            this.submit = false
            console.info('Notify update successfully') }
        })
      }
      else {
        //create
        this.notifier.username = this._authService.getUsername()
        this._notifierService.create(this.notifier).subscribe({
          next: (v) => {
            this.notifier = v
            this.notifierId = this.notifier.id ? this.notifier.id : 0
            this.title = 'Editar Notify'
            this._messageService.add({ severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('MessageService.Notify.CreateSuccess') })
            this.loading = false
            this.submit = false
          },
          error: (e) => {
            this.loading = false
            console.log(e)
            this._messageService.add({ severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('MessageService.Notify.CreateError') });
          },
          complete: () =>  {
            this.loading = false
            this.submit = false
            console.info('Notify create successfully')
          }
        })
      }
    }
    else {
      this.loading = false
    }
  }

  onChangeCountry() {

    this.formGroup.patchValue({
      cityId: null,
      contactCity: null
    });

    if(this.formGroup.controls['contactCountryId'].value) {
      this.loadCities(this.formGroup.controls['contactCountryId'].value)
    }
  }

  loadCities(countryId : any){
    this._cityService.getByCountry(countryId).subscribe({
      next: (v) => {
        this.cities = v;

        if (this.cities && this.cities.length > 0) {
          this.formGroup.get('city')?.setValidators([Validators.required]);
          this.formGroup.get('contactCity')?.clearValidators();
        } else {
          this.formGroup.get('contactCity')?.setValidators([Validators.required]);
          this.formGroup.get('city')?.clearValidators();
        }
        this.formGroup.get('city')?.updateValueAndValidity();
        this.formGroup.get('contactCity')?.updateValueAndValidity();
        },
      error: (e) => {
        this.cities = [];
        this.loading = false;
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.City.ManyRetrieveError') })
      },
      complete: () => console.info('Cities query successfully')
    })
  }

  cancel() {
    this._router.navigate([Routes.userNotifiers])
  }

  getErrorMessage(fieldName: string): string {
    if (this.formGroup.controls[fieldName].hasError('required')) {
      return this.translate.instant('FormErrorMessage.FieldValueRequired')
    }
    if (this.formGroup.controls[fieldName].hasError('email')) {
      return this.translate.instant('FormErrorMessage.EmailFieldEmailFormatError')
    }
    return ''
  }
}
